import React from 'react'
import ReactDOM from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter } from 'react-router-dom'
import { HelmetProvider } from 'react-helmet-async'
import { MaintenanceGate } from '@iberiavo/web-shared'
import { Toaster } from '@/components/ui/Toast'
import { ConfirmProvider } from '@/components/ui/ConfirmDialog'
import { registerServiceWorker } from '@/lib/pwa'
import { useAuthStore } from '@/store/authStore'
import { consumeBffHandoff } from './bootstrap/bff-handoff'
import './index.css'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
    },
  },
})

const MAINTENANCE_BFF =
  (import.meta.env.VITE_MAINTENANCE_BFF as string | undefined) ||
  (import.meta.env.VITE_BFF_BASE_URL as string | undefined) ||
  ''
const MAINTENANCE_TOOL =
  (import.meta.env.VITE_MAINTENANCE_TOOL as string | undefined) ||
  (import.meta.env.VITE_BFF_TOOL as string | undefined) ||
  'tvo'

function TvoMaintenanceGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user)
  const isAdmin = Boolean(
    (user as any)?.is_iberiavo_admin || (user as any)?.role === 'admin',
  )
  return (
    <MaintenanceGate
      bffBaseUrl={MAINTENANCE_BFF}
      isAdmin={isAdmin}
      brandName="TotalVO"
      tool={MAINTENANCE_TOOL}
    >
      {children}
    </MaintenanceGate>
  )
}

// Auth-2.0 Fase 2: canjea `?bff_handoff=<code>` por la cookie HttpOnly
// ANTES de importar App (que arrastra authStore + useCurrentUser que
// consultan /me al cargar).
async function bootstrap() {
  await consumeBffHandoff()
  const { default: App } = await import('./App')
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TvoMaintenanceGate>
            <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <ConfirmProvider>
                <App />
                <Toaster />
              </ConfirmProvider>
            </BrowserRouter>
          </TvoMaintenanceGate>
        </QueryClientProvider>
      </HelmetProvider>
    </React.StrictMode>,
  )
}

bootstrap()

// Registrar el Service Worker (no-op en DEV).
void registerServiceWorker()
